/**
 * Собственный набор значков — простые геометрические SVG без внешних
 * шрифтов и наборов иконок: собранная админка обязана быть самодостаточной
 * (раздел 6 спецификации порталов). Каждый значок декоративен (aria-hidden)
 * — подпись рядом всегда текстовая, значок её не заменяет.
 */

import type { ReactNode } from 'react'

export interface IconProps {
  className?: string
}

function base(children: ReactNode, className?: string) {
  return (
    <svg
      className={`icon${className !== undefined ? ` ${className}` : ''}`}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** Раздел «Инстансы» — устройство с антенной. */
export function IconInstances(props: IconProps) {
  return base(
    <>
      <rect x="4" y="6" width="12" height="9" rx="1.6" />
      <path d="M8 15v1.6M12 15v1.6" />
      <path d="M7 6 6 3M13 6l1-3" />
    </>,
    props.className,
  )
}

/** Раздел «Переписка» — пузырь диалога. */
export function IconChats(props: IconProps) {
  return base(
    <>
      <path d="M3.5 5.5h13a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H9l-3.5 3v-3H3.5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Z" />
    </>,
    props.className,
  )
}

/** Раздел «Контакты» — силуэт человека. */
export function IconContacts(props: IconProps) {
  return base(
    <>
      <circle cx="10" cy="6.5" r="3" />
      <path d="M4 17c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    </>,
    props.className,
  )
}

/** Раздел «Подписки» — колокольчик уведомлений. */
export function IconWebhooks(props: IconProps) {
  return base(
    <>
      <path d="M10 3.5c-2 0-3.5 1.6-3.5 3.6v2.3c0 .9-.4 1.7-1.1 2.3L4.5 12.7c-.5.4-.2 1.3.5 1.3h10c.7 0 1-.9.5-1.3l-.9-1c-.7-.6-1.1-1.4-1.1-2.3V7.1c0-2-1.5-3.6-3.5-3.6Z" />
      <path d="M8.3 16a1.8 1.8 0 0 0 3.4 0" />
    </>,
    props.className,
  )
}

/** Раздел «Сценарии» — три узла, соединённые связями. */
export function IconPipelines(props: IconProps) {
  return base(
    <>
      <circle cx="4.5" cy="10" r="2" />
      <circle cx="15.5" cy="5.5" r="2" />
      <circle cx="15.5" cy="14.5" r="2" />
      <path d="M6.5 9 13.5 6M6.5 11l7 3" />
    </>,
    props.className,
  )
}

/** Раздел «Credentials» — ключ. */
export function IconCredentials(props: IconProps) {
  return base(
    <>
      <circle cx="7" cy="7" r="3.2" />
      <path d="M9.3 9.3 16 16M13 14l1.5-1.5M15 12l1.5-1.5" />
    </>,
    props.className,
  )
}

/** Раздел «Переменные» — фигурные скобки выражения. */
export function IconVariables(props: IconProps) {
  return base(
    <>
      <path d="M8 4c-1.6 0-2 .9-2 2v2c0 1.1-.6 2-1.6 2 1 0 1.6.9 1.6 2v2c0 1.1.4 2 2 2" />
      <path d="M12 4c1.6 0 2 .9 2 2v2c0 1.1.6 2 1.6 2-1 0-1.6.9-1.6 2v2c0 1.1-.4 2-2 2" />
    </>,
    props.className,
  )
}

/** Раздел «Доступ в портал» — щит с ключевым отверстием. */
export function IconAccess(props: IconProps) {
  return base(
    <>
      <path d="M10 3 4.5 5v4.3c0 3.6 2.4 6.4 5.5 7.7 3.1-1.3 5.5-4.1 5.5-7.7V5L10 3Z" />
      <circle cx="10" cy="9" r="1.4" />
      <path d="M10 10.4V12.5" />
    </>,
    props.className,
  )
}

export function IconLogout(props: IconProps) {
  return base(
    <>
      <path d="M8.5 3.5H5a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h3.5" />
      <path d="M9 10h7.5M13.5 6.5 17 10l-3.5 3.5" />
    </>,
    props.className,
  )
}

export function IconClose(props: IconProps) {
  return base(
    <>
      <path d="M5 5l10 10M15 5 5 15" />
    </>,
    props.className,
  )
}

export function IconWarning(props: IconProps) {
  return base(
    <>
      <path d="M10 3.3 17.3 16H2.7L10 3.3Z" />
      <path d="M10 8.2v3.4" />
      <circle cx="10" cy="14" r="0.15" fill="currentColor" stroke="none" />
    </>,
    props.className,
  )
}

export function IconCheck(props: IconProps) {
  return base(
    <>
      <path d="M4 10.5 8 14.5 16 5.5" />
    </>,
    props.className,
  )
}

/** Идущий процесс: часы — используется в значках состояния "в процессе". */
export function IconClock(props: IconProps) {
  return base(
    <>
      <circle cx="10" cy="10" r="6.8" />
      <path d="M10 6v4l3 2" />
    </>,
    props.className,
  )
}

/** Тихий индикатор фонового обновления опросом — вращается через CSS. */
export function IconRefresh(props: IconProps) {
  return base(
    <>
      <path d="M15.5 6A6 6 0 1 0 16.7 11" />
      <path d="M15.5 2.8V6h-3.2" />
    </>,
    props.className,
  )
}

/** Светлая тема — солнце. */
export function IconSun(props: IconProps) {
  return base(
    <>
      <circle cx="10" cy="10" r="3.4" />
      <path d="M10 2.6v1.8M10 15.6v1.8M17.4 10h-1.8M4.4 10H2.6M15.2 4.8l-1.3 1.3M6.1 13.9l-1.3 1.3M15.2 15.2l-1.3-1.3M6.1 6.1 4.8 4.8" />
    </>,
    props.className,
  )
}

/** Тёмная тема — луна. */
export function IconMoon(props: IconProps) {
  return base(
    <>
      <path d="M16 11.6A6.4 6.4 0 0 1 8.4 4a6.6 6.6 0 1 0 7.6 7.6Z" />
    </>,
    props.className,
  )
}

/**
 * Тема из настроек системы — экран устройства: выбирает не человек в
 * портале, а его рабочее место.
 */
export function IconSystem(props: IconProps) {
  return base(
    <>
      <rect x="2.8" y="4" width="14.4" height="9.4" rx="1.4" />
      <path d="M7.4 16.6h5.2" />
    </>,
    props.className,
  )
}

/*
 * Стрелки сворачивания палитры нод (FR-ADM-044). Указывают туда, куда
 * поедет панель: влево — уедет за край, вправо — вернётся. Направление, а
 * не «плюс/минус»: сворачивание — это перемещение панели, а не действие
 * над её содержимым.
 */
export function IconChevronLeft(props: IconProps) {
  return base(<path d="M12.5 4.5 7 10l5.5 5.5" />, props.className)
}

export function IconChevronRight(props: IconProps) {
  return base(<path d="M7.5 4.5 13 10l-5.5 5.5" />, props.className)
}

/** Поиск — в командной палитре и подсказке о ней. */
export function IconSearch(props: IconProps) {
  return base(
    <>
      <circle cx="8.8" cy="8.8" r="5.2" />
      <path d="m12.7 12.7 4 4" />
    </>,
    props.className,
  )
}

export function IconBrand(props: IconProps) {
  return base(
    <>
      <path d="M3.5 5.5h13a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H9l-3.5 3v-3H3.5a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1Z" />
      <circle cx="7" cy="9" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="10" cy="9" r="0.4" fill="currentColor" stroke="none" />
      <circle cx="13" cy="9" r="0.4" fill="currentColor" stroke="none" />
    </>,
    props.className,
  )
}
